/**
 * What makes a popup a real modal dialog for a keyboard or screen-reader user (WCAG 2.1
 * SC 2.1.2, 2.4.3, 4.1.2; issue #362):
 *
 *  - when it opens, focus moves into it (to the element marked `data-autofocus`, else to
 *    the dialog itself, which then reads out its name);
 *  - Tab and Shift+Tab stay inside it, and focus that lands outside is brought back;
 *  - Escape closes it, unless the caller passes no `onEscape` (a request in flight);
 *  - when it closes, focus returns to the control that opened it;
 *  - it is named by its first heading when the caller gave it no name of its own.
 *
 * The dialog element carries `aria-modal="true"` and `tabIndex={-1}` itself; this hook
 * only moves focus. Nothing here depends on the clock or on the page's layout.
 */
import { useEffect, useRef, type RefObject } from 'react';

/** What a person can reach with Tab. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

let headingIds = 0;

/** The controls inside `root` that Tab reaches, in document order. */
export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => !element.closest('[inert],[hidden]'),
  );
}

/** Names the dialog by its first heading when it has no name of its own. */
export function labelByFirstHeading(dialog: HTMLElement): void {
  if (dialog.hasAttribute('aria-labelledby') || dialog.hasAttribute('aria-label')) return;
  const heading = dialog.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6');
  if (!heading) return;
  if (!heading.id) {
    headingIds += 1;
    heading.id = `modal-title-${headingIds}`;
  }
  dialog.setAttribute('aria-labelledby', heading.id);
}

/** The next element Tab should reach, or null when the browser's own move stays inside. */
function wrapTarget(dialog: HTMLElement, backwards: boolean): HTMLElement | null {
  const items = focusableIn(dialog);
  if (items.length === 0) return dialog;
  const first = items[0];
  const last = items[items.length - 1];
  const current = document.activeElement;
  if (!(current instanceof Node) || !dialog.contains(current)) return backwards ? last : first;
  if (backwards && (current === first || current === dialog)) return last;
  if (!backwards && current === last) return first;
  return null;
}

export function useModalFocus(
  dialogRef: RefObject<HTMLElement>,
  open: boolean,
  onEscape?: () => void,
): void {
  // Read when a key is pressed, so a caller's new handler never re-runs the focus move.
  const escape = useRef(onEscape);
  escape.current = onEscape;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return undefined;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    labelByFirstHeading(dialog);
    if (!dialog.contains(document.activeElement)) {
      (dialog.querySelector<HTMLElement>('[data-autofocus]') ?? dialog).focus();
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (escape.current) {
          event.preventDefault();
          escape.current();
        }
        return;
      }
      if (event.key !== 'Tab') return;
      const target = wrapTarget(dialog, event.shiftKey);
      if (target) {
        event.preventDefault();
        target.focus();
      }
    };
    const onFocusIn = (event: FocusEvent): void => {
      if (event.target instanceof Node && !dialog.contains(event.target)) dialog.focus();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      if (opener?.isConnected) opener.focus();
    };
  }, [dialogRef, open]);
}
